import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { Wallet, CreateWalletRequest, UpdateWalletRequest } from '../../../finance/models/Wallet';
import axiosInstance from '@/setup/axios/axiosInstance';

interface WalletState {
  wallets: Wallet[];
  loading: boolean;
  error: string | null;
}

const initialState: WalletState = {
  wallets: [],
  loading: false,
  error: null,
};

// No mock data - using real API only

// Async thunk to fetch wallets
export const fetchWalletsThunk = createAsyncThunk(
  'wallets/fetchWallets',
  async (params?: { company?: string; company_id?: string; no_pagination?: boolean }) => {
    const response = await axiosInstance.get('/finance/wallets/', { params });
    
    // Handle pagination response format
    if (response.data && typeof response.data === 'object' && 'results' in response.data) {
      return response.data.results;
    }
    
    // Handle direct array response
    return response.data;
  }
);

// Async thunk to create wallet
export const createWalletThunk = createAsyncThunk(
  'wallets/createWallet',
  async (data: CreateWalletRequest) => {
    const response = await axiosInstance.post('/finance/wallets/', data);
    return response.data;
  }
);

// Async thunk to update wallet
export const updateWalletThunk = createAsyncThunk(
  'wallets/updateWallet',
  async (data: UpdateWalletRequest) => {
    const response = await axiosInstance.put(`/finance/wallets/${data.id}/`, data);
    return response.data;
  }
);

// Async thunk to delete wallet
export const deleteWalletThunk = createAsyncThunk(
  'wallets/deleteWallet',
  async (id: string) => {
    await axiosInstance.delete(`/finance/wallets/${id}/`);
    return id;
  }
);

// Create slice
const walletSlice = createSlice({
  name: 'wallets',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch wallets
    builder
      .addCase(fetchWalletsThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWalletsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.wallets = action.payload;
      })
      .addCase(fetchWalletsThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch wallets';
      });

    // Create wallet
    builder
      .addCase(createWalletThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWalletThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.wallets.unshift(action.payload);
      })
      .addCase(createWalletThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create wallet';
      });

    // Update wallet
    builder
      .addCase(updateWalletThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWalletThunk.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.wallets.findIndex(w => w.id === action.payload.id);
        if (index !== -1) {
          state.wallets[index] = action.payload;
        }
      })
      .addCase(updateWalletThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update wallet';
      });

    // Delete wallet
    builder
      .addCase(deleteWalletThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteWalletThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.wallets = state.wallets.filter(w => w.id !== action.payload);
      })
      .addCase(deleteWalletThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete wallet';
      });
  },
});

export const { clearError } = walletSlice.actions;
export const reducer = walletSlice.reducer;

// Selectors
export const selectWallets = (state: { wallets: WalletState }) => state.wallets.wallets;
export const selectWalletLoading = (state: { wallets: WalletState }) => state.wallets.loading;
export const selectWalletError = (state: { wallets: WalletState }) => state.wallets.error;

// Export actions object for compatibility
export const actions = {
  requestWallets: () => ({ type: 'wallets/requestWallets' }),
  fulfillWallets: (wallets: Wallet[]) => ({ 
    type: 'wallets/fulfillWallets', 
    payload: wallets 
  }),
  createWallet: (wallet: Wallet) => ({ 
    type: 'wallets/createWallet', 
    payload: wallet 
  }),
  updateWallet: (wallet: Wallet) => ({ 
    type: 'wallets/updateWallet', 
    payload: wallet 
  }),
  deleteWallet: (id: string) => ({ 
    type: 'wallets/deleteWallet', 
    payload: id 
  }),
};
